import styled, { useTheme } from 'styled-components';
import { BodyMedium, BodySmall, ButtonMediumSemiText } from '../Texts';
import Button from '../Button';
import { hapticsImpactLight } from '../../common/haptic';
import { usePrivy, useSignupWithPasskey } from '@privy-io/react-auth';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { openModal } from '../../redux/slices/modals';
import {
  EVENT_DEPOSIT_MODAL_OPENED,
  EVENT_WITHDRAW_MODAL_OPENED,
  NAVBAR,
  sendEvent,
} from '../../common/analytics';
import { formatUSD } from '../../common/currency';
import { selectTotalWalletBalanceUSD } from '../../redux/slices/tokenSlice';
import { selectPrivyToken } from '../../redux/slices/auth';
import {
  useGetCurrentUserProfileQuery,
  useTestPushNotificationMutation,
} from '../../redux/api/authorized/auth';
import Line from '../Line';
import { useSolana } from '../../hooks/useSolana';
import { useContext } from 'react';
import { useFlag } from '@unleash/proxy-client-react';
import { PHONE_MEDIA_QUERY, useMediaQuery } from '../../common/responsive';
import { IonMenuToggle } from '@ionic/react';
import { useLogout } from '../../hooks/useLogout';
import { selectThemeMode, toggleTheme } from '../../redux/slices/themeSlice';
import UserProfileIcon from '../../assets/icons/user-profile.svg?react';
import SunIcon from '../../assets/icons/sun.svg?react';
import MoonIcon from '../../assets/icons/moon.svg?react';
import CardIcon from '../../assets/icons/card-2.svg?react';
import DownloadIcon from '../../assets/icons/download.svg?react';
import GiftFilledIcon from '../../assets/icons/gift-filled.svg?react';
import SettingsIcon from '../../assets/icons/settings.svg?react';
import ExperimentIcon from '../../assets/icons/experiment.svg?react';
import AdminIcon from '../../assets/icons/admin.svg?react';
import UsersIcon from '../../assets/icons/users.svg?react';
import PrinterIcon from '../../assets/icons/printer.svg?react';
import RewardsFilledIcon from '../../assets/icons/rewards-filled.svg?react';
import { Capacitor } from '@capacitor/core';

const DropdownContentContainer = styled.div`
  padding: 0 5%;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    padding-top: 80px;
    gap: 4px;
  }
`;

const BalanceContainer = styled.div`
  display: flex;
  cursor: pointer;
  align-items: center;
  justify-content: space-between;
  gap: 10px;

  padding: 10px;
  background: ${({ theme }) => theme.background.secondary};
  min-height: 46px;
  border-radius: 8px;
`;

const DropdownElement = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 10px;
  flex-direction: row;
  width: 100%;
  cursor: pointer;
  margin-top: 10px;
  @media ${PHONE_MEDIA_QUERY} {
    margin-top: 0px;
    padding-top: 10px;
    padding-bottom: 10px;
    border-radius: 8px;
    align-items: center;
  }
`;

const InnerContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  @media ${PHONE_MEDIA_QUERY} {
    gap: 10px;
    margin-bottom: 10px;
  }
`;

const CloseMenu = ({ children }) => {
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);
  const ExtraElement = isMobile ? IonMenuToggle : 'div';
  return <ExtraElement style={{ width: '100%' }}>{children}</ExtraElement>;
};

const UserInfoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const DropdownContent = ({ setIsOpen }) => {
  const theme = useTheme();
  const themeMode = useSelector(selectThemeMode);
  const navigate = useNavigate();
  const totalBalance = useSelector(selectTotalWalletBalanceUSD);
  const dispatch = useDispatch();
  const { user } = usePrivy();
  const { externalWallet } = useSolana();
  const enabledPasskeyLogin = useFlag('enabledPasskeyLogin');
  const { signupWithPasskey } = useSignupWithPasskey();

  const enableWalletConnect = useFlag('enableWalletConnect');
  const { handleLogout, handleDisconnectWallet } = useLogout(setIsOpen);
  const privyToken = useSelector(selectPrivyToken);
  const { data: currentUser } = useGetCurrentUserProfileQuery(undefined, {
    skip: !privyToken,
  });

  const handleDeposit = async () => {
    dispatch(openModal({ name: 'addFunds' }));
    sendEvent(EVENT_DEPOSIT_MODAL_OPENED, {
      from: NAVBAR,
    });
  };

  const handleWithdraw = async () => {
    dispatch(openModal({ name: 'withdrawFunds' }));
    sendEvent(EVENT_WITHDRAW_MODAL_OPENED, {
      from: NAVBAR,
    });
  };

  const [testPushNotification] = useTestPushNotificationMutation();

  const location = useLocation();
  const currentPath = location.pathname;
  const isMobile = useMediaQuery(PHONE_MEDIA_QUERY);

  const email = currentUser?.email;
  const phoneNumber = currentUser?.phoneNumber;

  return (
    <DropdownContentContainer>
      <InnerContainer>
        {isMobile && (
          <UserInfoContainer>
            <UserProfileIcon
              width={48}
              height={48}
              style={{ color: theme.text.regular }}
            />
            <BodySmall>
              {email ? (
                <>
                  {email.split('@')[0]}
                  <span style={{ userSelect: 'none' }}>&#8203;</span>
                  <span>@</span>
                  {email.split('@')[1]}
                </>
              ) : (
                <>{phoneNumber}</>
              )}
            </BodySmall>
          </UserInfoContainer>
        )}
        <CloseMenu>
          <BalanceContainer
            onClick={() => {
              hapticsImpactLight();
              navigate('/app/portfolio');
              if (setIsOpen) {
                setIsOpen(false);
              }
            }}
          >
            <BodySmall>Balance</BodySmall>
            <BodySmall>{formatUSD(totalBalance)}</BodySmall>
          </BalanceContainer>
        </CloseMenu>
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            gap: 10,
            marginTop: 10,
          }}
        >
          <CloseMenu>
            <Button onClick={handleDeposit} fullWidth={true}>
              Deposit
            </Button>
          </CloseMenu>
          <CloseMenu>
            <Button
              onClick={handleWithdraw}
              fullWidth={true}
              variant='secondary'
            >
              Withdraw
            </Button>
          </CloseMenu>
        </div>
      </InnerContainer>
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/portfolio');
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <CardIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>My Portfolio</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {!Capacitor.isNativePlatform() && (
        <>
          {isMobile && <Line />}
          <CloseMenu>
            <DropdownElement
              onClick={() => {
                hapticsImpactLight();
                dispatch(openModal({ name: 'downloadApp' }));
                if (setIsOpen) {
                  setIsOpen(false);
                }
              }}
            >
              <DownloadIcon
                width={20}
                height={20}
                style={{ color: theme.text.regular }}
              />
              <BodyMedium>Download App</BodyMedium>
            </DropdownElement>
          </CloseMenu>
        </>
      )}
      {isMobile && <Line />}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            dispatch(toggleTheme());
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          {themeMode === 'dark' ? (
            <SunIcon
              width={20}
              height={20}
              style={{ color: theme.text.regular }}
            />
          ) : (
            <MoonIcon
              width={20}
              height={20}
              style={{ color: theme.text.regular }}
            />
          )}
          <BodyMedium>
            {themeMode === 'dark' ? 'Light mode' : 'Dark mode'}
          </BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {Capacitor.isNativePlatform() && enabledPasskeyLogin && (
        <>
          {isMobile && <Line />}
          <CloseMenu>
            <DropdownElement
              onClick={() => {
                hapticsImpactLight();
                signupWithPasskey();
              }}
            >
              <PrinterIcon
                width={20}
                height={20}
                style={{ color: theme.text.regular }}
              />
              <BodyMedium>Configure Passkey</BodyMedium>
            </DropdownElement>
          </CloseMenu>
        </>
      )}
      {isMobile && <Line />}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            navigate('/gift-cards');
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <PrinterIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>Physical Gift Cards</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {isMobile && <Line />}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            navigate('/app/gifts');
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <GiftFilledIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>My Gifts</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {isMobile && <Line />}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            navigate('/rewards');
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <RewardsFilledIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>Rewards</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {isMobile && <Line />}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            dispatch(openModal({ name: 'settings' }));
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <SettingsIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>Settings</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      {isMobile && <Line />}
      {currentUser?.role === 'ADMIN' && (
        <>
          <CloseMenu>
            <DropdownElement
              onClick={() => {
                hapticsImpactLight();
                testPushNotification(currentUser?.email);
                if (setIsOpen) {
                  setIsOpen(false);
                }
              }}
            >
              <ExperimentIcon
                width={20}
                height={20}
                style={{ color: theme.text.regular }}
              />
              <BodyMedium>Test Notification</BodyMedium>
            </DropdownElement>
          </CloseMenu>
          {isMobile && <Line />}
          <CloseMenu>
            <DropdownElement
              onClick={() => {
                hapticsImpactLight();
                navigate('/admin');
                if (setIsOpen) {
                  setIsOpen(false);
                }
              }}
            >
              <AdminIcon
                width={20}
                height={20}
                style={{ color: theme.text.regular }}
              />
              <BodyMedium>Admin Dashboard</BodyMedium>
            </DropdownElement>
          </CloseMenu>
          {isMobile && <Line />}
        </>
      )}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            dispatch(openModal({ name: 'referrals' }));
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <UsersIcon
            width={20}
            height={20}
            style={{ color: theme.text.regular }}
          />
          <BodyMedium>Referrals</BodyMedium>
        </DropdownElement>
      </CloseMenu>
      <Line />
      {externalWallet && enableWalletConnect && (
        <CloseMenu>
          <DropdownElement onClick={handleDisconnectWallet}>
            <ButtonMediumSemiText style={{ color: theme.text.secondary }}>
              Disconnect Wallet
            </ButtonMediumSemiText>
          </DropdownElement>
        </CloseMenu>
      )}
      <CloseMenu>
        <DropdownElement
          onClick={() => {
            hapticsImpactLight();
            handleLogout();
            if (setIsOpen) {
              setIsOpen(false);
            }
          }}
        >
          <ButtonMediumSemiText style={{ color: theme.text.secondary }}>
            Logout
          </ButtonMediumSemiText>
        </DropdownElement>
      </CloseMenu>
    </DropdownContentContainer>
  );
};

export default DropdownContent;
